/**
 * TrendChart — multi-series line chart for the traction dashboard.
 *
 * Hand-rolled inline SVG rather than a charting dependency: four series over a
 * zero-filled date spine needs no library, and this keeps the bundle flat.
 *
 * Palette is the validated 4-hue categorical set (teal / blue / amber /
 * violet). It passes the lightness band, chroma floor, CVD separation and
 * contrast checks; blue↔teal sits at ΔE 6.4 for tritanopia, which is inside
 * the floor band and therefore legal ONLY with secondary encoding — hence the
 * mandatory legend AND direct end-labels on every series. Do not drop those.
 *
 * Marks: 2px lines with non-scaling strokes, 9px markers on hover, recessive
 * grid, crosshair + tooltip, no number printed on every point.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { text, border, bg } from '../../theme/colors';

const PAD = { top: 12, right: 46, bottom: 22, left: 34 };
const HEIGHT = 180;

/**
 * Integer axis that fits the data.
 *
 * A fixed floor (the previous `max(5)`) wasted most of the plot whenever the
 * peak was 1 or 2, and a fixed 25% grid produced fractional tick labels for
 * counts. This picks an integer step from a 1/2/5 ladder so the top gridline
 * sits just above the peak and every label is a whole number.
 */
const buildAxis = (peak) => {
  const safePeak = Math.max(1, peak);
  const targetTicks = 4;
  const raw = safePeak / targetTicks;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * mag).find((c) => c >= raw) || 10 * mag;
  const stepInt = Math.max(1, Math.round(step));
  const max = Math.ceil(safePeak / stepInt) * stepInt;
  const ticks = [];
  for (let v = 0; v <= max; v += stepInt) ticks.push(v);
  return { max, ticks };
};

const formatTick = (iso, bucket) => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: bucket === 'day' ? 'numeric' : undefined,
    month: 'short',
    ...(bucket === 'week' ? { day: 'numeric' } : {}),
    timeZone: 'UTC',
  });
};

const TrendChart = ({ series = [], seriesConfig = [], bucket = 'day' }) => {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(720);
  const [hoverIndex, setHoverIndex] = useState(null);

  // Plain resize listener rather than ResizeObserver — this repo carries
  // known RO loop-error workarounds, so avoid adding another observer.
  useEffect(() => {
    const measure = () => {
      if (wrapRef.current) setWidth(wrapRef.current.offsetWidth || 720);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const plot = useMemo(() => {
    const innerW = Math.max(120, width - PAD.left - PAD.right);
    const innerH = HEIGHT - PAD.top - PAD.bottom;
    const peak = Math.max(
      0,
      ...series.flatMap((p) => seriesConfig.map((c) => p[c.key] || 0))
    );
    const { max, ticks } = buildAxis(peak);
    const n = series.length;
    const x = (i) => PAD.left + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
    const y = (v) => PAD.top + innerH - (v / max) * innerH;
    return { innerW, innerH, max, ticks, x, y, n };
  }, [width, series, seriesConfig]);

  if (series.length === 0 || seriesConfig.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: text.tertiary, py: 4, textAlign: 'center' }}>
        No activity in this period.
      </Typography>
    );
  }

  const { ticks, x, y, innerH } = plot;
  // Aim for ~5 x labels regardless of how many buckets there are.
  const tickEvery = Math.max(1, Math.ceil(series.length / 5));

  const hovered = hoverIndex != null ? series[hoverIndex] : null;

  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const ratio = (px - PAD.left) / Math.max(1, plot.innerW);
    const idx = Math.round(ratio * (series.length - 1));
    setHoverIndex(Math.min(series.length - 1, Math.max(0, idx)));
  };

  return (
    <Box>
      {/* Legend — always present for >= 2 series; identity never colour-alone. */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        {seriesConfig.map((c) => (
          <Box key={c.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: text.secondary, fontWeight: 500 }}>
              {c.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box ref={wrapRef} sx={{ position: 'relative', width: '100%' }}>
        <svg
          width="100%"
          height={HEIGHT}
          viewBox={`0 0 ${width} ${HEIGHT}`}
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIndex(null)}
          style={{ display: 'block', overflow: 'visible' }}
          role="img"
          aria-label={`Trend of ${seriesConfig.map((c) => c.label).join(', ')}`}
        >
          {/* Recessive grid + y labels */}
          {ticks.map((value) => {
            const gy = y(value);
            return (
              <g key={value}>
                <line
                  x1={PAD.left}
                  x2={width - PAD.right}
                  y1={gy}
                  y2={gy}
                  stroke={border.light}
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={PAD.left - 6}
                  y={gy + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill={text.tertiary}
                >
                  {value}
                </text>
              </g>
            );
          })}

          {/* x labels */}
          {series.map((p, i) => (
            i % tickEvery === 0 || i === series.length - 1 ? (
              <text
                key={p.date}
                x={x(i)}
                y={HEIGHT - PAD.bottom + 15}
                textAnchor="middle"
                fontSize="11"
                fill={text.tertiary}
              >
                {formatTick(p.date, bucket)}
              </text>
            ) : null
          ))}

          {/* Crosshair */}
          {hoverIndex != null && (
            <line
              x1={x(hoverIndex)}
              x2={x(hoverIndex)}
              y1={PAD.top}
              y2={PAD.top + innerH}
              stroke={border.default}
              strokeWidth="1"
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
          )}

          {/* Series */}
          {seriesConfig.map((c) => {
            const points = series.map((p, i) => `${x(i)},${y(p[c.key] || 0)}`).join(' ');
            const lastValue = series[series.length - 1]?.[c.key] || 0;
            return (
              <g key={c.key}>
                <polyline
                  points={points}
                  fill="none"
                  stroke={c.color}
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
                {/* Direct end-label: the secondary encoding the palette requires. */}
                <text
                  x={width - PAD.right + 6}
                  y={y(lastValue) + 4}
                  fontSize="11"
                  fontWeight="600"
                  fill={c.color}
                >
                  {lastValue}
                </text>
                {hoverIndex != null && (
                  <circle
                    cx={x(hoverIndex)}
                    cy={y(series[hoverIndex][c.key] || 0)}
                    r="4.5"
                    fill={c.color}
                    stroke={bg.paper}
                    strokeWidth="2"
                    vectorEffect="non-scaling-stroke"
                  />
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {hovered && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: Math.min(
                Math.max(0, x(hoverIndex) - 70),
                Math.max(0, width - 150)
              ),
              pointerEvents: 'none',
              bgcolor: bg.paper,
              border: `1px solid ${border.light}`,
              borderRadius: 1.5,
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
              px: 1.25,
              py: 1,
              minWidth: 140,
            }}
          >
            <Typography variant="caption" sx={{ color: text.secondary, fontWeight: 600, display: 'block', mb: 0.5 }}>
              {formatTick(hovered.date, bucket)}
            </Typography>
            {seriesConfig.map((c) => (
              <Box key={c.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.color, flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: text.secondary }} noWrap>
                    {c.label}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: text.primary, fontWeight: 700 }}>
                  {hovered[c.key] || 0}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TrendChart;
